/**
 * The hub card. Prop-free by design — the integrator drops <VerseCard /> into the home
 * screen and it owns its own reads, its own states and its own refresh.
 *
 * This is set like scripture, not like a stat tile: serif, generous line height, room to
 * breathe, and the attribution api.bible requires us to display always present.
 *
 * Every state tells the truth. A failed read is never rendered as a verse, an empty cache
 * is never rendered as an all-clear, and a stale verse SAYS it's stale rather than quietly
 * passing for this week's.
 */
import { useId, useState } from 'react';
import { Link } from 'react-router-dom';
import { useIsAdult } from '../../lib/identity';
import type { VerseConfig, VerseOfWeek } from '../../shared/types';
import { BookIcon, Button, Note, Skeleton, useAsync } from './ui';
import { explain, getConfig, getVerse, postRefresh, prettyDate, thisMonday } from './verseApi';

export default function VerseCard() {
  const adult = useIsAdult();
  const headingId = useId();

  const verse = useAsync<VerseOfWeek | null>('verse', getVerse);
  const config = useAsync<VerseConfig>('verse-config', getConfig);

  const [refreshing, setRefreshing] = useState(false);
  const [refreshError, setRefreshError] = useState<string | null>(null);

  async function refresh() {
    setRefreshing(true);
    setRefreshError(null);
    try {
      verse.set(await postRefresh());
      config.reload();
    } catch (err) {
      setRefreshError(explain(err, 'The refresh did not go through. Try again in a moment.'));
    } finally {
      setRefreshing(false);
    }
  }

  const loading = verse.loading || config.loading;
  const failed = verse.error !== null || config.error !== null;

  return (
    <section
      aria-labelledby={headingId}
      className="overflow-hidden rounded-card border border-border bg-gradient-to-br from-honey-soft/70 via-surface to-surface shadow-[0_1px_3px_rgba(43,39,35,0.05)]"
    >
      <div className="p-6 sm:p-7">
        <h2
          id={headingId}
          className="flex items-center gap-2 text-xs font-semibold tracking-[0.12em] text-ink-soft uppercase"
        >
          <BookIcon className="h-4 w-4 text-honey" />
          Verse of the week
        </h2>

        <div className="mt-5">
          {loading && (
            <div className="space-y-3" role="status" aria-live="polite">
              <span className="sr-only">Loading this week&rsquo;s verse…</span>
              <Skeleton className="h-5 w-full" />
              <Skeleton className="h-5 w-11/12" />
              <Skeleton className="h-5 w-2/3" />
              <Skeleton className="mt-5 h-4 w-32" />
            </div>
          )}

          {!loading && failed && (
            <div className="space-y-3">
              <Note tone="warn" role="alert">
                Couldn&rsquo;t load this week&rsquo;s verse.
              </Note>
              <Button
                onClick={() => {
                  verse.reload();
                  config.reload();
                }}
              >
                Try again
              </Button>
            </div>
          )}

          {!loading && !failed && config.data && (
            <Body
              verse={verse.data}
              config={config.data}
              adult={adult}
              refreshing={refreshing}
              refreshError={refreshError}
              onRefresh={refresh}
            />
          )}
        </div>
      </div>
    </section>
  );
}

function Body({
  verse,
  config,
  adult,
  refreshing,
  refreshError,
  onRefresh,
}: {
  verse: VerseOfWeek | null;
  config: VerseConfig;
  adult: boolean;
  refreshing: boolean;
  refreshError: string | null;
  onRefresh: () => void;
}) {
  // A cached verse is rendered FIRST, whatever the config says. The text lives in D1 and
  // needs no api.bible key to be read — so a key that's been rotated or pulled must never
  // blank the scripture on the family's hub and replace it with a setup nag. Setup lives in
  // Settings; if something is broken, the adults get told UNDER the verse, not instead of it.
  if (verse) {
    const stale = verse.week_start < thisMonday();
    const broken = !config.configured || !config.bible_id || config.last_error !== null;

    return (
      <div>
        <figure>
          <blockquote className="font-display text-[1.375rem] leading-[1.75] text-ink sm:text-2xl sm:leading-[1.8]">
            &ldquo;{verse.text}&rdquo;
          </blockquote>
          <figcaption className="mt-5 space-y-2">
            <p className="flex flex-wrap items-baseline gap-x-2">
              <cite className="font-display text-base font-semibold text-ink not-italic">
                {verse.reference}
              </cite>
              {verse.translation && (
                <>
                  <span aria-hidden="true" className="text-ink-faint">
                    ·
                  </span>
                  <span className="text-sm text-ink-soft">{verse.translation}</span>
                </>
              )}
            </p>
            {/* api.bible requires the attribution it returns be displayed. */}
            <p className="text-[11px] leading-snug text-ink-faint">
              {verse.copyright ??
                `Scripture from api.bible${verse.translation ? ` (${verse.translation})` : ''}.`}
            </p>
          </figcaption>
        </figure>

        {(stale || (adult && broken)) && (
          <div className="mt-5 space-y-3 border-t border-border pt-4">
            {/* Name the week we actually have. We know when this verse is from; we do NOT
                know that it is "last week's" — after an outage it can be far older. */}
            {stale && (
              <Note tone="info">
                This week&rsquo;s verse hasn&rsquo;t landed yet. This one is from the week of{' '}
                {prettyDate(verse.week_start)}.
              </Note>
            )}
            {adult && !config.configured && (
              <Note tone="warn">api.bible isn&rsquo;t connected — the Worker needs its key.</Note>
            )}
            {adult && config.configured && (
              <RefreshRow
                refreshing={refreshing}
                refreshError={refreshError}
                lastError={config.last_error}
                onRefresh={onRefresh}
                label="Get this week's verse"
              />
            )}
          </div>
        )}
      </div>
    );
  }

  // Nothing cached. Now — and only now — the card is a setup screen.

  // 1. No key on the Worker. Say that — don't dress it up as "coming soon".
  if (!config.configured) {
    return (
      <Setup>
        <p className="text-sm leading-relaxed text-ink-soft">
          The verse of the week isn&rsquo;t connected yet.{' '}
          {adult
            ? 'It needs the api.bible key on the Worker.'
            : 'A grown-up still has to finish setting it up.'}
        </p>
        {adult && <SettingsLink label="Finish setting it up" />}
      </Setup>
    );
  }

  // 2. Key, but nobody has chosen a translation. Brian picks — we never pick for him.
  // No verse count here: the plan is editable config and this card cannot read the
  // adults-only plan endpoint, so any number would be a guess.
  if (!config.bible_id) {
    return (
      <Setup>
        <p className="text-sm leading-relaxed text-ink-soft">
          {adult
            ? 'Pick a translation and the reading plan starts — one verse every Monday.'
            : 'A grown-up needs to choose a translation. Then a new verse lands every Monday.'}
        </p>
        {adult && <SettingsLink label="Choose a translation" />}
      </Setup>
    );
  }

  // 3. Chosen, nothing fetched yet. The refresh button belongs right here.
  return (
    <Setup>
      <p className="text-sm leading-relaxed text-ink-soft">
        The first verse lands Monday morning.{adult ? ' Or bring it in now.' : ''}
      </p>
      {adult && (
        <RefreshRow
          refreshing={refreshing}
          refreshError={refreshError}
          lastError={config.last_error}
          onRefresh={onRefresh}
          label="Get this week's verse"
        />
      )}
      {!adult && config.last_error && (
        <Note tone="info">A grown-up needs to have a look — it didn&rsquo;t load this week.</Note>
      )}
    </Setup>
  );
}

function Setup({ children }: { children: React.ReactNode }) {
  return <div className="space-y-4">{children}</div>;
}

function SettingsLink({ label }: { label: string }) {
  return (
    <Link
      to="/admin"
      className="inline-flex min-h-11 items-center rounded-full bg-sage px-4 text-sm font-medium text-white transition-colors hover:bg-sage/90 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-sage"
    >
      {label}
    </Link>
  );
}

function RefreshRow({
  refreshing,
  refreshError,
  lastError,
  onRefresh,
  label,
}: {
  refreshing: boolean;
  refreshError: string | null;
  lastError: string | null;
  onRefresh: () => void;
  label: string;
}) {
  return (
    <div className="space-y-3">
      <Button variant="primary" onClick={onRefresh} disabled={refreshing}>
        {refreshing ? 'Fetching…' : label}
      </Button>
      {/* The reason it failed, in the API's own words. Never a guess. */}
      {refreshError && (
        <Note tone="warn" role="alert">
          {refreshError}
        </Note>
      )}
      {!refreshError && lastError && <Note tone="warn">{lastError}</Note>}
    </div>
  );
}
